import { AxiosError } from 'axios'
import { intersection, mapValues, compact, omit } from 'lodash'
import { defineStore } from 'pinia'
import { arrExcludePrefixes, arrFilterPrefixes, toggleValue } from '@/utils/arrayUtils'
import { deserializeJsonApi } from '@/utils/jsonApiDeserializer'
import { watch } from 'vue'
import { until } from '@vueuse/core'
import { useRoute, useRouter, type Router } from 'vue-router'
import { createApi } from '@/utils/api'
import { purposesAndVibes } from '@/dataset/infoCollectionData'
import { signedIncrement } from '@/utils/numberUtils'
import { Country } from '@/models'
import { getAttributionData } from '@/utils/attribution'
import { replaceLocation } from '@/utils/browserNavigation'
import { logErrorOnSentry } from '@/utils/errorReportingUtils'
import { createItineraryRequestApiPayload } from '@/utils/itineraryRequestApiTransform'

let api = createApi()
const TEXT_SEPARATOR = '\n\n\n'

// Router captured while an injection context is active. Stale-session
// recovery runs from Axios continuations where `useRouter()`/`useRoute()`
// return undefined, so the recovery action must never call the composables.
let staleSessionRouter: Router | null = null

// Country aliases to add as separate destination options
const COUNTRY_ALIASES = [
  {
    parentCountryCode: 'ID',
    alias: {
      feature_images: [],
      country_code: 'ID',
      continent: 'Asia',
      name: 'Bali',
      price: {
        cents: 110000,
        currency_iso: 'USD',
      },
      nature: 2,
      safety: 'Moderate',
      min_temperature: 75,
      max_temperature: 89,
      flag: '🇮🇩',
    },
  },
]

export const useInfoCollectionData = defineStore('info-collection-data', {
  state: () => ({
    loading: false,
    initialized: false,
    fullpanel: false,
    id: null as string | number | null,
    pendingSaves: [] as Promise<any>[],
    purposes: [] as string[],
    vibes: [] as string[],
    otherVibe: '',
    tripProfile: '',
    vibeSummary: '',
    groupTypes: [] as string[],
    groupTypeOther: '',
    group: '',
    destType: '',
    destCountries: [] as string[],
    allCountryList: [] as Country[],
    countryFilterNotice: null as string | null,
    countryFilterType: null as string | null,
    dates: {
      type: 'flexible',
      specific: { startDate: null, endDate: null },
      duration: 0,
      durationUnit: 'day',
      months: [] as any[],
    },
    inspiration: [] as string[],
    budget: null as string | null,
    budgetArr: null as any[] | null,
    flowCompleted: false,
    validating: null as string | null,
    errors: {} as { [key: string]: boolean | null },
    countryInfo: {} as { [key: string]: string },
    noMatch: false,
    airtableItineraryId: '',
    userId: null as string | null,
    // Signed ActionCable channel name issued by the API; the server rejects
    // subscriptions to names built from the bare id.
    vibeSummaryChannelId: null as string | null,
    // Signed Pusher channel name issued by the API; the server refuses to
    // sign names built from the bare id.
    recommendationChannelName: null as string | null,
    travelPace: null as string | null,
    accommodations: [] as string[],
    foodExperiences: [] as string[],
    source: null as string | null,
    staleSessionRecovered: false,
  }),

  getters: {
    sessionLatestRoute(state): string {
      if (!state.purposes?.length) return 'purpose'
      if (!this.vibeStepComplete) return 'vibe'
      if (!state.inspiration) return 'inspiration'
      if (!state.travelPace) return 'pace-of-travel'
      if (state.accommodations.length === 0) return 'accommodation'
      if (state.foodExperiences.length === 0) return 'food-experiences'
      if (!state.vibeSummary) return 'trip-profile'
      if (!state.group) return 'group'
      if (!state.destType) return 'destination'
      if (state.destType === 'specific' && state.destCountries.length === 0) return 'destination'
      if (state.dates.type === 'flexible' && state.dates.months.length === 0) return 'dates'
      if (state.dates.type === 'specific' && (!state.dates.specific.startDate || !state.dates.specific.endDate))
        return 'dates'
      if (!state.budget) {
        return 'budget'
      }

      return 'outro'
    },
    vibeStepComplete(state): boolean {
      const selectedPurposes = Object.entries(purposesAndVibes).filter(([key]) => state.purposes.includes(key))
      const hasSelectableVibes = selectedPurposes.some(([, data]) => Array.isArray(data.vibes) && data.vibes.length > 0)
      const requiresOtherVibe = selectedPurposes.some(([, data]) => data.vibes === null)
      const allowWithoutSelections = !hasSelectableVibes && !requiresOtherVibe

      return Boolean(state.vibes.length || state.otherVibe || selectedPurposes.length === 0 || allowWithoutSelections)
    },
    vibeCount(state): number {
      return state.vibes.length
    },
    destCountryList(state) {
      return compact(
        state.destCountries.map(country =>
          state.allCountryList.find(c => c.country_code === country || c.name === country),
        ),
      )
    },
    errorCount(state): number {
      return Object.values(state.errors).filter(Boolean).length
    },
    validationCompleted(state): boolean {
      return Object.values(state.errors).every(e => typeof e === 'boolean')
    },
    // transform for api attribute formats...
    apiPayload(state): object {
      const attributionData = getAttributionData()
      return createItineraryRequestApiPayload(state, {
        flowCompleted: this.flowCompleted,
        tracking: attributionData || {},
        source: state.source ?? 'product_plan_a_trip',
      })
    },
    hasSelectedFoodVibes(state): boolean {
      const foodVibes = purposesAndVibes['Food & flavor'].vibes
      return intersection(state.vibes, foodVibes).length > 0
    },
  },

  actions: {
    async load(taskFn: () => Promise<void>) {
      try {
        this.loading = true
        await taskFn()
      } finally {
        this.loading = false
      }
    },

    async initialize(id: string | number) {
      // Recovery is one-way for this page's lifetime: the hard replacement is
      // already in flight, so no continuation may re-arm the store.
      if (this.staleSessionRecovered) return
      // A channel id issued for a previous request must never outlive a switch
      // to a new one; subscribers wait for the new request's own channel id.
      if (this.id !== null && String(this.id) !== String(id)) this.vibeSummaryChannelId = null
      this.id = id
      // Sync is the authoritative session check and, for deal_<id>
      // references, the only resolver of the canonical itinerary-request ID;
      // destinations must not start until it succeeds, so a sync failure or
      // stale recovery can never populate dependent country state.
      await this.syncFromApi(id)
      if (this.staleSessionRecovered) return
      await this.loadCountryList()
      if (this.staleSessionRecovered) return
      await this.loadCountryInfo()
      if (this.staleSessionRecovered) return
      this.initialized = true
    },

    async initializeWithDealId(id: number) {
      if (this.staleSessionRecovered) return
      if (this.id !== null && String(this.id) !== String(id)) this.vibeSummaryChannelId = null
      this.id = id
      await this.syncFromApi(id)
      await this.loadCountryList()
      if (this.staleSessionRecovered) return
      this.initialized = true
    },

    syncState(data: { [key: string]: any }) {
      const sameRequest = this.id === Number(data.id)
      this.id = Number(data.id)
      this.airtableItineraryId = data.airtable_itinerary_id ?? ''
      this.noMatch = data.no_match ?? false
      this.purposes = data.purposes ?? []
      this._syncVibes(data.vibes)
      this.tripProfile = data.trip_profile ?? ''
      this.vibeSummary = data.vibe_summary ?? ''
      this.inspiration = (data.inspiration ?? '').split(TEXT_SEPARATOR).filter(Boolean)
      this._syncGroupType(data.group_type)
      this.group = data.group ?? ''
      this._syncDestination(data.country ?? '')
      this._syncDates(data.dates ?? {})
      this._syncBudget(data.budget, data.minimum_budget_cents, data.maximum_budget_cents)
      this.userId = data.user_id ?? null
      this.source = data.source ?? null
      // Keep the last issued name only for the same request; a different
      // request must not inherit another trip's channel.
      this.vibeSummaryChannelId = data.vibe_summary_channel_id ?? (sameRequest ? this.vibeSummaryChannelId : null)
      this.recommendationChannelName =
        data.recommendation_channel_name ?? (sameRequest ? this.recommendationChannelName : null)
    },

    _syncVibes(vibes) {
      this.vibes = arrExcludePrefixes(vibes, ['Other', 'Pace:', 'Accommodation:', 'Food:'])
      this.otherVibe = arrFilterPrefixes(vibes, ['Other:'], true).join(', ')
      this.travelPace = arrFilterPrefixes(vibes, ['Pace:'], true).join(', ')
      this.accommodations = arrFilterPrefixes(vibes, ['Accommodation:'], true)
      this.foodExperiences = arrFilterPrefixes(vibes, ['Food:'], true)
    },

    _syncDestination(country: string) {
      if (country === 'all') {
        this.destType = 'flexible'
        this.destCountries = []
      } else if (country.length > 0) {
        this.destType = 'specific'
        this.destCountries = country.split(',')
      }
    },

    _syncGroupType(groupType: string | string[] | null | undefined) {
      const parts = Array.isArray(groupType)
        ? groupType
        : (groupType || '')
            .split(',')
            .map(p => p.trim())
            .filter(Boolean)

      const normalized = parts.map(p => p.toString().trim()).filter(Boolean)
      const nonOther = normalized.filter(p => !p.toLowerCase().startsWith('other'))
      const otherEntry = normalized.find(p => p.toLowerCase().startsWith('other'))
      this.groupTypes = nonOther
      if (otherEntry) {
        this.groupTypes.push('Other')
        this.groupTypeOther = otherEntry.replace(/Other:\s*/i, '')
      } else {
        this.groupTypeOther = ''
      }
    },

    _syncDates({
      date_type = 'flexible',
      duration = '1 day',
      duration_unit = 'day',
      flexible_dates = {},
      specific_dates = [],
    }): void {
      this.dates.type = date_type
      this.dates.specific = {
        startDate: specific_dates?.[0] ?? null,
        endDate: specific_dates?.[1] ?? null,
      }
      let [durationValue, durationUnit] = String(duration).split(' ')
      durationUnit ??= duration_unit

      this.dates.duration = Number(durationValue)
      this.dates.durationUnit = durationUnit
      this.dates.months = (() => {
        const mapFn = (months: any[], year: string) => months.map((month: string) => ({ month, year }))
        const months = mapValues(flexible_dates, mapFn)
        return Object.values(months).flat()
      })()
    },

    _syncBudget(budget, minBudgetCents, maxBudgetCents) {
      this.budget = budget
      this.budgetArr = [minBudgetCents ? minBudgetCents / 100 : null, maxBudgetCents ? maxBudgetCents / 100 : null]
    },

    async trackPendingOperation(operation: () => Promise<any>) {
      const promise = operation()

      this.pendingSaves.push(promise)

      try {
        return await promise
      } finally {
        const index = this.pendingSaves.indexOf(promise)
        if (index > -1) {
          this.pendingSaves.splice(index, 1)
        }
      }
    },

    // Components that intentionally do not await a save must go through
    // saveToApiInBackground below — Pinia re-wraps every action's returned
    // promise, so an ignored rejection here would surface as unhandled.
    async saveToApi(sync = false) {
      return this.trackPendingOperation(async () => {
        if (this.staleSessionRecovered) return
        const payload = this.apiPayload
        const { data } = this.id
          ? await updateItineraryRequest(this.id, payload)
          : await createItineraryRequest(payload)

        // A save that was already in flight when recovery won must not write
        // the ID back (which would repersist it through the watcher) or
        // restart initialization.
        if (this.staleSessionRecovered) return
        const sameRequest = this.id === Number(data.id)
        this.id = Number(data.id) // this must be here in case local state becomes out of sync
        this.vibeSummaryChannelId = data.vibe_summary_channel_id ?? (sameRequest ? this.vibeSummaryChannelId : null)
        this.recommendationChannelName =
          data.recommendation_channel_name ?? (sameRequest ? this.recommendationChannelName : null)

        if (!this.initialized) await this.initialize(Number(data.id))
        // Recovery inside the awaited initialization wins: syncing the server
        // payload back would repopulate the reset store and repersist the
        // stale id through the watcher.
        if (this.staleSessionRecovered) return
        if (sync || this.airtableItineraryId != (data.airtableItineraryId ?? '')) this.syncState(data)
      })
    },

    // The fire-and-forget boundary: a breaking save failure is reported to
    // Sentry instead of becoming an unhandled rejection.
    saveToApiInBackground(sync = false) {
      this.saveToApi(sync).catch((error: unknown) => logErrorOnSentry(error, { name: 'infoCollection.saveToApi' }))
    },

    // The request-scoped endpoints reject a browser whose Rails session no
    // longer owns the persisted itinerary id: legacy responses use 403 and
    // the session-bound destinations endpoint answers a uniform 404.
    _isStaleSessionError(err: unknown): boolean {
      return err instanceof AxiosError && (err.response?.status === 403 || err.response?.status === 404)
    },

    _captureStaleSessionRouter(router: Router | null) {
      staleSessionRouter = router
    },

    _recoverFromStaleSession() {
      if (this.staleSessionRecovered) return
      const router = staleSessionRouter

      this.$reset()
      this.staleSessionRecovered = true
      setPlanTripId(null, planTripIdForPath(window.location.pathname))
      if (!router) {
        logErrorOnSentry(new Error('Stale session recovery ran without a captured router'), {
          name: 'infoCollection',
        })
        return
      }
      const route = router.currentRoute.value
      // Drop the resume keys so the stale ID cannot be re-derived from the
      // URL; keep unrelated attribution/query parameters.
      const query = omit(route.query, ['reference_id', 'itinerary_request_id', 'id'])
      // Hard same-origin replacement instead of router navigation: it cannot
      // be re-widened by the router's query carry-forward guard, replaces the
      // history entry, and forces a full remount into a clean session even
      // when the current route is already `start`.
      const resolved = router.resolve({ name: 'start', params: route.params, query })
      replaceLocation(resolved.href)
    },

    async syncFromApi(id: string | number | null = null) {
      let data: Record<string, unknown> | undefined

      try {
        data = await getItineraryRequest(id ?? (this.id as any))
      } catch (err: unknown) {
        if (this._isStaleSessionError(err)) {
          this._recoverFromStaleSession()
          return
        }
        // A non-stale failure must not degrade into a reset store that later
        // reports itself initialized; the caller decides how to surface it.
        console.error(err)
        throw err
      }

      if (this.staleSessionRecovered) return

      if (data) {
        this.syncState(data)
      } else {
        this.$reset()
      }
    },

    async loadCountryList(force = false) {
      if (!force && this.allCountryList.length > 0) return
      if (!this.id) return

      let data: DestinationsResponseData
      try {
        const response = await api.get<DestinationsResponseData>('/api/plan_a_trip/destinations', {
          params: { itinerary_request_id: this.id },
        })
        data = response.data
      } catch (err: unknown) {
        if (this._isStaleSessionError(err)) {
          this._recoverFromStaleSession()
          return
        }
        throw err
      }

      if (this.staleSessionRecovered) return

      const countries = (Array.isArray(data) ? data : data?.countries) ?? []
      const meta = Array.isArray(data) ? {} : (data?.meta ?? {})

      this.countryFilterNotice = meta?.notice ?? null
      this.countryFilterType = meta?.filter_type ?? null
      this.allCountryList = countries
      this._addCountryAliases()
    },

    _addCountryAliases() {
      COUNTRY_ALIASES.forEach(({ parentCountryCode, alias }) => {
        const hasParentCountry = this.allCountryList.some(c => c.country_code === parentCountryCode)
        if (hasParentCountry) {
          this.allCountryList.push(alias)
        }
      })
    },

    async getSeasonsFromApi(): Promise<any[]> {
      if (this.destType !== 'specific' || this.destCountries.length !== 1) return []
      const query = this.destCountries.map(c => `country_codes[]=${c}`).join('&')
      const { data } = await api.get(`/api/plan_a_trip/season?${query}`)
      return data
    },

    async loadCountryInfo(): Promise<void> {
      if (this.destType === 'specific') {
        const query = this.destCountries.map(c => `country_codes[]=${c}`).join('&')
        const {
          data: { countries },
        } = await api.get(`/api/countries/season_descriptions?${query}`)
        if (this.staleSessionRecovered) return
        this.countryInfo = countries
      }
    },

    togglePurpose(purpose: string, force: boolean | null = null): boolean {
      this.purposes = toggleValue(this.purposes, purpose, force)
      const purposeConfig = purposesAndVibes[purpose]
      const vibes = purposeConfig?.vibes ?? []
      if (!this.purposes.includes(purpose)) vibes.forEach(vibe => this.toggleVibe(vibe, false))
      return this.purposes.includes(purpose)
    },

    toggleVibe(vibe: string, force: boolean | null = null): boolean {
      this.vibes = toggleValue(this.vibes, vibe, force)
      return this.vibes.includes(vibe)
    },

    toggleGroupType(groupType: string, force: boolean | null = null): boolean {
      this.groupTypes = toggleValue(this.groupTypes, groupType, force)
      return this.groupTypes.includes(groupType)
    },

    async generateVibeSummary(prompt: string = '') {
      const url = '/api/plan_a_trip/vibe_summary'

      // TODO: Error handling
      await api.put(
        url,
        {
          prompt,
          itinerary_request_id: this.id,
        },
        {
          headers: { Accept: 'application/json' },
        },
      )
    },

    async reportErrors(validationMode = 'full'): Promise<boolean> {
      this.validating = validationMode
      await until(() => this.validationCompleted).toBe(true)
      return this.errorCount > 0
    },

    async waitForPendingSaves() {
      if (this.pendingSaves.length > 0) {
        await Promise.all(this.pendingSaves)
      }
    },

    async requestRecommendation() {
      await this.waitForPendingSaves()
      const url = '/api/plan_a_trip/recommendation'
      return await api.post(url, {
        itinerary_request_id: this.id,
      })
    },

    resetApi(csrfToken: any) {
      api = createApi(csrfToken)
    },
  },
})

export const useInfoCollectionStore = defineStore('info-collection', () => {
  const store = useInfoCollectionData()

  const route = useRoute()
  const router = useRouter()
  store._captureStaleSessionRouter(router)

  /**
   * HACK: This is a hack to prevent plan-a-trip-chat from redirecting to start page of plan-a-trip
   * due to app/javascript/src/modules/InfoCollection/Components/InputValidation.vue used by plan-a-trip-chat making use
   * of useInfoCollectionStore.
   *
   * This approach is very much not ideal, but it's the best we can do for now. There needs to be a better way to standardize
   * handling of authentication with a centralized authentication store instead of using a feature store.
   */
  const id = getPlanTripId(planTripIdForPath(route.path))

  const getRoute = (step: number): string => {
    const order = route.meta.order as number
    const routeName = router.getRoutes().find(r => r.meta.order === order + step)?.name
    if (!routeName) throw Error(`${String(route.name)} + (${step}) is missing!`)
    return String(routeName)
  }

  const goRouteByOrder = async (step: number) => {
    let routeName = getRoute(step)
    while (shouldSkipRoute(routeName)) {
      routeName = getRoute(signedIncrement(step))
    }
    router.push({ name: routeName })
  }

  if (id && store.id !== id) {
    // Bootstrap failure is a breaking error: report it without converting it
    // into stale recovery; the store stays uninitialized.
    store.initialize(id).catch((err: unknown) => {
      logErrorOnSentry(err, { name: 'infoCollection' })
    })
  }

  if (!id && route.name !== 'start') {
    router.push({ name: 'start' })
  }

  watch(
    () => store.id,
    id => {
      if (id) setPlanTripId(id, planTripIdForPath(route.path))
    },
  )

  watch(route, () => {
    store.validating = null
  })

  function onInitialized(callback: () => void) {
    return until(() => store.initialized)
      .toBeTruthy()
      .then(callback)
  }

  function onFirstInitialized(callback: () => void) {
    if (!store.initialized) onInitialized(callback)
  }

  function onSubsequentInitialized(callback: () => void) {
    if (store.initialized) onInitialized(callback)
  }

  function shouldSkipRoute(routeName: string): boolean {
    // if in vibes page user selected food preference, then skip this page altogether.
    if (routeName === 'food-experiences' && store.hasSelectedFoodVibes) {
      return true
    }
    return false
  }

  return {
    store: store,
    onInitialized,
    onFirstInitialized,
    onSubsequentInitialized,
    goPrev: () => goRouteByOrder(-1),
    goNext: () => goRouteByOrder(+1),
  }
})

interface DestinationsMeta {
  notice?: string | null
  filter_type?: string | null
}

type DestinationsResponseData = Country[] | { countries?: Country[]; meta?: DestinationsMeta }

// Do not change this ID, so that localStorage is backward compatible
export enum PlanATripId {
  PlanATrip = 'plan-a-trip:id',
  PlanATripChat = 'plan-a-trip-chat:id',
}

// Route-family discriminator: decide between the legacy plan-a-trip flow and
// the chat flow from the route path only (never fullPath/query text).
function planTripIdForPath(path: string): PlanATripId {
  return path.includes('plan-a-trip-legacy') ? PlanATripId.PlanATrip : PlanATripId.PlanATripChat
}

export function setPlanTripId(id: string | number | null, type: PlanATripId) {
  if (id) {
    localStorage.setItem(type, String(id))
  } else {
    localStorage.removeItem(type) // make sure to update store state too
  }
}

export function getPlanTripId(type: PlanATripId): number | string | null {
  const searchParams = new URLSearchParams(location.search)
  const refId = searchParams.get('reference_id')
  if (refId) {
    return `deal_${refId}`
  }
  const candidates = [
    searchParams.get('itinerary_request_id'),
    searchParams.get('id'),
    localStorage.getItem(type),
  ].filter((value): value is string => Boolean(value))

  for (const candidate of candidates) {
    if (candidate === 'null' || candidate === 'undefined') continue

    const numericCandidate = Number(candidate)
    if (!Number.isNaN(numericCandidate)) {
      return numericCandidate
    }

    if (candidate.startsWith('deal_')) {
      return candidate
    }
  }

  return null
}

export async function getItineraryRequest(id: string | number) {
  const { data } = await api.get(`/api/hero/itinerary_requests/${id}`)
  return deserializeJsonApi(data, 'snake_case')
}

export async function createItineraryRequest(payload: object) {
  const { data } = await api.post(`/api/hero/itinerary_requests`, payload)
  return { data: await deserializeJsonApi(data, 'snake_case'), meta: data.meta }
}

export async function updateItineraryRequest(id: string | number, payload: object) {
  const { data } = await api.put(`/api/hero/itinerary_requests/${id}`, payload)
  return { data: await deserializeJsonApi(data, 'snake_case'), meta: data.meta }
}
